import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'

import { CreateCampaignWrapper } from '../../../Components/Campaigns/CreateEditCampaign/CreateEditCampaignWrapper'
import { LayoutDashboard } from '../../../Components/Layout/Dashboard'
import { PageTitle } from '../../../Components/PageTitle'

import { CreateEditCampaignForm } from '../../Form/Campaigns/CreateEditCampaign'
import PreBuild from '../../Form/Campaigns/CreateEditCampaign/PreBuild'
import { HideWhenBlocked } from '../../UIBlock/HideWhenBlocked'

import {
  createCampaign,
  updateCampaign,
  getCampaignById,
} from '../../../Reducers/Campaigns/actions'

import { getClients } from '../../../Reducers/Clients/actions'
import { uploadPitchFile } from '../../../Reducers/Upload/actions'

import CampaignsIcon from '../../../Assets/Images/icons/campaigns'

import { promisify } from '../../../Utils/Actions'

import { PATHS } from '../../../Config/Routing/paths'
import {
  parseCreateCampaignFormValues,
  parseUpdateCampaignFormValues,
  getInitialFormValues,
  validationCampaign,
} from './util'
import {
  runCreateCampaignSideEffects,
  runUpdateCampaignSideEffects,
} from './sideEffects'
import { CAMPAIGN_ENUM_TYPES, TAG_TYPE_ENUM } from '../../../shared/constants'
import { showReduxModal } from '../../../Reducers/UI/actions'
import {
  getCampaignCityMetrics,
  getCampaignMetricsTagsGQL,
  updateCampaignCityMetrics,
  updateCampaignTargeting,
} from './gql'
import { normalizeGraphQLConnection } from '../../../shared/graphql/utils'
import { apiXero } from '../../../Module/API/Xero'
import { formatCityMetrics } from '../../Form/Campaigns/CreateEditCampaign/util'
import StyledBreadcrumbs from '../../../Components/StyledBreadcrumbs'
import { apiClient } from '../../../Module/API/Client'
import CampaignStandbySettings from './CampaignStandbySettings'

const CreateCampaign = props => {
  const { clients, editMode } = props
  const title = editMode ? 'Edit Campaign' : 'Create Campaign'

  const isXeroAvailable = apiXero.isHaveXeroAccess()
  const [campaign, setCampaign] = useState(null)
  const [loadingData, setLoadingData] = useState({
    isLoading: false,
    label: 'Loading...',
  })
  const isEDDM =
    campaign &&
    campaign.isSelfServe &&
    campaign.type === CAMPAIGN_ENUM_TYPES.DIRECTMAIL
  const [campaignCityMetrics, setCampaignCityMetrics] = useState([])

  const [metrics, setMetrics] = useState([
    {
      id: 'new_metric_1',
      label: '',
      name: 'Orders',
      targetConversionRate: null,
      targetCostPerMetric: null,
    },
  ])
  const [oldMetrics, setOldMetrics] = useState([])
  var [primaryMetricIndex, setPrimaryMetricIndex] = useState(0)
  var [missions, setMissions] = useState(null)
  const [defaultMetrics, setDefaultMetrics] = useState([])

  useEffect(() => {
    props.getClients()
    getDefaultMetrics()
    if (props.editMode) {
      getCampaignData()
    }
  }, [])

  const getDefaultMetrics = async () => {
    const res = await getCampaignMetricsTagsGQL({
      type: TAG_TYPE_ENUM.CAMPAIGN_METRIC,
    })
    const result = await res.json()
    const data = normalizeGraphQLConnection(result.data.getTags)
    setDefaultMetrics(data)
  }

  const getCampaignCityMetricsData = async () => {
    if (campaign && campaign.id) {
      const campaignCityMetricsData = await getCampaignCityMetrics(campaign.id)
      setCampaignCityMetrics(campaignCityMetricsData)
    }
  }

  useEffect(() => {
    getCampaignCityMetricsData()
  }, [campaign])

  const getCampaignData = async () => {
    setLoadingData({ isLoading: true, label: "Loading campaign's data" })
    props
      .getCampaignById(props.match.params.campaignId, {
        includeMissions: true,
      })
      .then(async response => {
        var {
          metrics: metricsRaw,
          cityPlans,
          ...campaign
        } = response.data.campaign
        var missions = cityPlans.nodes.map(c =>
          c.missions.edges.map(edge => ({
            ...edge.node,
            cityPlanId: c.id,
          })),
        )
        setMissions(missions.flat())
        setCampaign(campaign)

        var metrics = metricsRaw.nodes.map((m, i) => {
          var { isPrimary, ...metric } = m
          if (isPrimary) setPrimaryMetricIndex(i)
          return metric
        })
        setMetrics([])
        setMetrics(metrics)
        setOldMetrics(metrics)
        setLoadingData({ isLoading: false, label: '' })
      })
  }

  const onSubmitHandler = async values => {
    // Map in the metrics
    values = Object.assign({}, values)

    const isValid = await validationCampaign({
      values,
      metrics,
      primaryMetricIndex,
      showReduxModal: props.showReduxModal,
    })

    if (!isValid) {
      return
    }

    props.editMode
      ? await editCampaignFormSubmitHandler(values)
      : await createCampaignFormSubmitHandler(values)
  }

  const createCampaignFormSubmitHandler = async values => {
    setLoadingData({
      isLoading: true,
      label: 'Creating campaign...',
    })
    const { createCampaign } = props
    const parsedValues = parseCreateCampaignFormValues(values)
    try {
      const response = await createCampaign(parsedValues)
      const {
        data: {
          createCampaign: { campaign },
        },
      } = response

      if (values.citiesMetrics.length > 0) {
        const citiesMetrics = formatCityMetrics(values.citiesMetrics)
        await updateCampaignCityMetrics({
          campaignId: campaign.id,
          data: citiesMetrics,
        })
      }

      if (values.targeting) {
        await updateCampaignTargeting({
          campaignId: Number(campaign.id),
          data: values.targeting,
        })
      }

      if (values.insertsCostMetrics.length > 0) {
        await apiClient.updateInsertsCostMetrics({
          campaignId: Number(campaign.id),
          insertsCostMetrics: values.insertsCostMetrics,
        })
      }

      if (campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX) {
        setLoadingData({
          isLoading: true,
          label: 'Creating city plans and missions...',
        })
      }
      await runCreateCampaignSideEffects({
        campaign,
        values,
        props,
        parsedValues,
        isXeroAvailable,
      })
      createCampaignFormSubmitSuccessHandler(campaign.id)
    } catch (error) {
      setLoadingData({
        isLoading: false,
      })
      // Error on this page is always throwed into alert from redux but lets also show error in the console
      console.error(error)
      return
    }
  }

  const createCampaignFormSubmitSuccessHandler = id => {
    const { history } = props
    setLoadingData({
      isLoading: false,
      label: '',
    })
    history.push(
      `${PATHS.main.dashboard}${
        PATHS.dashboard.campaigns
      }/${id}/dashboard/overview`,
    )
  }

  const editCampaignFormSubmitHandler = async values => {
    setLoadingData({
      isLoading: true,
      label: 'Saving updated campaign...',
    })
    const {
      match,
      updateCampaign,
      uploadPitchFile: uploadPitchFileFunction,
    } = props

    const uploadPitchFile = promisify(uploadPitchFileFunction)
    if (values.citiesMetrics.length > 0) {
      const citiesMetrics = formatCityMetrics(values.citiesMetrics)

      await updateCampaignCityMetrics({
        campaignId: campaign.id,
        data: citiesMetrics,
      })
    }
    if (values.targeting) {
      await updateCampaignTargeting({
        campaignId: Number(campaign.id),
        data: values.targeting,
      })
    }

    if (values.insertsCostMetrics.length > 0) {
      await apiClient.updateInsertsCostMetrics({
        campaignId: Number(campaign.id),
        insertsCostMetrics: values.insertsCostMetrics,
      })
    }

    if (values.pitchUrl instanceof Array) {
      const pitchFile = values.pitchUrl[0]
      const pitchUrl = await uploadPitchFile(campaign.id, pitchFile)
      values.pitchUrl = pitchUrl
    } else {
      values.pitchUrl = campaign.pitchUrl
    }

    const parsedValues = parseUpdateCampaignFormValues(
      match.params.campaignId,
      values,
    )
    try {
      await updateCampaign(parsedValues)
      if (campaign.type === CAMPAIGN_ENUM_TYPES.LETTERBOX) {
        setLoadingData({
          isLoading: true,
          label: 'Creating city plans and missions...',
        })
      }

      await runUpdateCampaignSideEffects({
        campaign,
        values,
        props,
        oldMetrics,
        parsedValues,
        primaryMetricIndex,
      })
    } catch (error) {
      setLoadingData({
        isLoading: false,
      })
      // Error on this page is always throwed into alert from redux but lets also show error in the console
      console.error(error)
      throw new Error(error)
    }

    await getCampaignData()
  }

  const loaded = !editMode || (editMode && campaign)
  const formInitialValues = getInitialFormValues(campaign, editMode)

  const onMetricUpdate = (id, key, value) => {
    setMetrics(prev =>
      prev.map(metric => {
        if (key == 'name') {
          return metric.id == id
            ? {
                ...metric,
                label: '',
                [key]: value,
              }
            : { ...metric }
        } else {
          return metric.id == id
            ? {
                ...metric,
                [key]: value,
              }
            : { ...metric }
        }
      }),
    )
  }

  var CampaignViewComponent = editMode ? CreateEditCampaignForm : PreBuild

  const breadcrumbs = [
    {
      label: 'Campaigns',
      link: `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}?Status=Active`,
    },
    ...(!isEDDM && editMode
      ? [
          {
            label: campaign ? campaign.name : 'Loading name...',
            link:
              campaign && campaign.type === CAMPAIGN_ENUM_TYPES.INSERTS
                ? `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
                    campaign.id
                  }/dashboard`
                : `${PATHS.main.dashboard}${PATHS.dashboard.campaigns}/${
                    props.match.params.campaignId
                  }${PATHS.campaign.cityPlans}`,
          },
        ]
      : []),
    {
      label: title,
    },
  ]

  return (
    <LayoutDashboard>
      <HideWhenBlocked>
        <CreateCampaignWrapper>
          <StyledBreadcrumbs
            icon={<CampaignsIcon style={{ width: 16 }} />}
            breadcrumbs={breadcrumbs}
          />
          <PageTitle className="page-title">{title}</PageTitle>
          {loaded ? (
            <CampaignViewComponent
              {...formInitialValues}
              defaultMetrics={defaultMetrics}
              campaign={campaign}
              cities={props.cities}
              clients={clients}
              campaignCityMetrics={campaignCityMetrics}
              getCampaignCityMetricsData={getCampaignCityMetricsData}
              onSubmit={onSubmitHandler}
              editMode={editMode}
              initialValues={formInitialValues}
              loading={loadingData.isLoading}
              loadingLabel={loadingData.label}
              canDeletePrimaryMetric={!editMode || !campaign}
              // By pass redux form confusing cludge
              metrics={metrics}
              onMetricUpdate={onMetricUpdate}
              onMetricDelete={id => {
                const newMetrics = metrics.filter(metric => metric.id !== id)
                setMetrics(newMetrics)
              }}
              onMetricAdd={() => {
                setMetrics([
                  ...metrics,
                  { id: `new_metric_${metrics.length + 1}` },
                ])
              }}
              onPrimaryMetricIndexChange={setPrimaryMetricIndex}
              primaryMetricIndex={primaryMetricIndex}
              missions={missions}
            />
          ) : null}
          {editMode && campaign && campaign.id ? (
            <CampaignStandbySettings campaignId={campaign.id} />
          ) : null}
        </CreateCampaignWrapper>
      </HideWhenBlocked>
    </LayoutDashboard>
  )
}

CreateCampaign.propTypes = {
  clients: PropTypes.array.isRequired,
  createCampaign: PropTypes.func.isRequired,
  getClients: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
}

const mapStateToProps = state => ({
  clients: state.clients.clientsList,
  cities: state.city.citiesList,
})

export default connect(
  mapStateToProps,
  {
    createCampaign,
    updateCampaign,
    getClients,
    uploadPitchFile,
    getCampaignById,
    showReduxModal,
  },
)(withRouter(CreateCampaign))
